// frontend/src/app/components/onboarding/series-rebaseline-panel.tsx
//
// Stage 11 admin panel for SERIES REBASELINES (backend
// app/services/series_rebaseline.py). A rebaseline is one planned, dated change
// to a client's measurement series: lane pin changes, lanes added or removed,
// question edits, belief/rubric changes, and a note saying why. It is drafted
// here, reviewed in a confirmation that lists everything it will change,
// applied once, taken by the next client read (the new baseline), and can never
// be edited afterwards.
//
// The panel shows the series' full rebaseline history, the one open draft (if
// any) as an editor, and the apply flow. Applying needs the digest of the
// preview the admin just read, so what gets written is exactly what was
// confirmed. If anything moved in between, the server refuses and asks for a
// fresh review.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  GitCommitVertical, Loader2, Plus, Save, Trash2, Undo2, X, ShieldAlert,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

import {
  applyRebaseline, cancelRebaseline, createRebaseline, formatRebaselineDate,
  listRebaselines, previewRebaseline, updateRebaseline,
  type CurrentSeries, type QuestionChangeSpec, type RebaselinePreview,
  type RebaselineSpec, type SeriesRebaseline, type VariableChangeSpec,
} from "@/api/series-rebaseline";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

const VIOLET = "var(--spectral-violet)";

const box: React.CSSProperties = {
  backgroundColor: "var(--background)", border: "1px solid var(--border)", borderRadius: 8,
};
const label: React.CSSProperties = {
  display: "block", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
  letterSpacing: "0.04em", color: "var(--muted-foreground)", marginBottom: 4,
};
const input: React.CSSProperties = {
  width: "100%", padding: "6px 10px", fontSize: "var(--text-sm)", color: "var(--foreground)",
  backgroundColor: "var(--background)", border: "1px solid var(--border)", borderRadius: 6, outline: "none",
};
const btn = (primary = false, disabled = false): React.CSSProperties => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 6,
  fontSize: "var(--text-xs)", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.55 : 1,
  border: primary ? "none" : "1px solid var(--border)",
  backgroundColor: primary ? VIOLET : "transparent",
  color: primary ? "white" : "var(--foreground)",
});

// The lane whose web_search tool version a rebaseline can move (#159).
const CLAUDE_LANE = "claude";

const CATEGORIES = ["direct", "problem", "feature", "comparison", "economics", "integration"];
const VARIABLE_FIELDS = ["claim", "score_2", "score_1", "score_0"] as const;

// ── The editable draft, held as UI state and converted to a spec on save ────

interface QuestionEdit { text: string }
interface VariableEdit { claim: string; score_2: string; score_1: string; score_0: string }

interface DraftState {
  note: string;
  lanes: string[];
  pins: Record<string, string>;            // lane -> chosen version ("" = keep)
  claudeSearchTool: string;                // "" = keep the series' version
  questionEdits: Record<string, QuestionEdit>;
  questionRemovals: string[];
  questionAdds: { text: string; category: string }[];
  variableEdits: Record<string, VariableEdit>;
  variableRemovals: string[];
  variableAdds: { id: string; claim: string }[];
}

function emptyDraft(current: CurrentSeries): DraftState {
  return {
    note: "", lanes: [...current.lanes], pins: {}, claudeSearchTool: "", questionEdits: {}, questionRemovals: [],
    questionAdds: [], variableEdits: {}, variableRemovals: [], variableAdds: [],
  };
}

function draftFromSpec(spec: RebaselineSpec, note: string, current: CurrentSeries): DraftState {
  const d = emptyDraft(current);
  d.note = note;
  if (spec.lanes) d.lanes = [...spec.lanes];
  d.pins = { ...(spec.lane_pins ?? {}) };
  d.claudeSearchTool = spec.claude_search_tool ?? "";
  for (const c of spec.question_changes ?? []) {
    if (c.op === "edit" && c.text != null) d.questionEdits[c.query_id] = { text: c.text };
    if (c.op === "remove") d.questionRemovals.push(c.query_id);
    if (c.op === "add") d.questionAdds.push({ text: c.query.text, category: c.query.category ?? "direct" });
  }
  for (const c of spec.variable_changes ?? []) {
    if (c.op === "edit") {
      const v = current.reasoning_variables.find((x) => x.id === c.variable_id);
      d.variableEdits[c.variable_id] = {
        claim: c.fields.claim ?? v?.claim ?? "",
        score_2: c.fields.score_2 ?? v?.score_2 ?? "",
        score_1: c.fields.score_1 ?? v?.score_1 ?? "",
        score_0: c.fields.score_0 ?? v?.score_0 ?? "",
      };
    }
    if (c.op === "remove") d.variableRemovals.push(c.variable_id);
    if (c.op === "add") d.variableAdds.push({ id: c.variable.id, claim: c.variable.claim });
  }
  return d;
}

function specFromDraft(d: DraftState, current: CurrentSeries): RebaselineSpec {
  const question_changes: QuestionChangeSpec[] = [];
  for (const [query_id, edit] of Object.entries(d.questionEdits)) {
    const q = current.queries.find((x) => x.id === query_id);
    if (q && edit.text.trim() && edit.text.trim() !== q.text) {
      question_changes.push({ op: "edit", query_id, text: edit.text.trim() });
    }
  }
  for (const query_id of d.questionRemovals) question_changes.push({ op: "remove", query_id });
  for (const add of d.questionAdds) {
    if (add.text.trim()) question_changes.push({ op: "add", query: { text: add.text.trim(), category: add.category } });
  }
  const variable_changes: VariableChangeSpec[] = [];
  for (const [variable_id, edit] of Object.entries(d.variableEdits)) {
    const v = current.reasoning_variables.find((x) => x.id === variable_id);
    if (!v) continue;
    const fields: Record<string, string> = {};
    for (const f of VARIABLE_FIELDS) {
      if ((edit[f] ?? "") !== ((v as any)[f] ?? "")) fields[f] = edit[f];
    }
    if (Object.keys(fields).length) variable_changes.push({ op: "edit", variable_id, fields });
  }
  for (const variable_id of d.variableRemovals) variable_changes.push({ op: "remove", variable_id });
  for (const add of d.variableAdds) {
    if (add.id.trim() && add.claim.trim()) {
      variable_changes.push({ op: "add", variable: { id: add.id.trim(), claim: add.claim.trim() } });
    }
  }
  const lane_pins = Object.fromEntries(
    Object.entries(d.pins).filter(([lane, v]) => v && d.lanes.includes(lane)),
  );
  const spec: RebaselineSpec = { lanes: d.lanes, lane_pins, question_changes, variable_changes };
  if (d.claudeSearchTool && d.lanes.includes(CLAUDE_LANE)) spec.claude_search_tool = d.claudeSearchTool;
  return spec;
}

// ── Panel ───────────────────────────────────────────────────────────────────

export function SeriesRebaselinePanel({
  customerId,
  onApplied,
}: {
  customerId: number;
  /** Called after a successful apply, so the run list and policy refresh. */
  onApplied: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rebaselines, setRebaselines] = useState<SeriesRebaseline[]>([]);
  const [current, setCurrent] = useState<CurrentSeries | null>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<RebaselinePreview | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [applying, setApplying] = useState(false);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  const openDraft = useMemo(() => rebaselines.find((r) => r.status === "draft") ?? null, [rebaselines]);
  const history = useMemo(() => rebaselines.filter((r) => r.status !== "draft"), [rebaselines]);
  const appliedCount = useMemo(() => rebaselines.filter((r) => r.status === "applied").length, [rebaselines]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listRebaselines(customerId);
      setRebaselines(res.rebaselines);
      setCurrent(res.current);
      setUnavailable(null);
      const d = res.rebaselines.find((r) => r.status === "draft");
      setDraft(d ? draftFromSpec(d.spec ?? {}, d.note, res.current) : null);
    } catch (e: any) {
      setUnavailable(e?.message || "Could not load rebaselines");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const saveDraft = async (): Promise<SeriesRebaseline | null> => {
    if (!draft || !current) return null;
    setSaving(true);
    try {
      const body = { spec: specFromDraft(draft, current), note: draft.note };
      const res = openDraft
        ? await updateRebaseline(customerId, openDraft.id, body)
        : await createRebaseline(customerId, body);
      setRebaselines((prev) => [...prev.filter((r) => r.id !== res.rebaseline.id), res.rebaseline]);
      toast.success("Rebaseline draft saved.");
      return res.rebaseline;
    } catch (e: any) {
      toast.error(e?.message || "Failed to save the draft");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const review = async () => {
    const saved = await saveDraft();
    if (!saved) return;
    try {
      setConfirmed(false);
      setPreview(await previewRebaseline(customerId, saved.id));
    } catch (e: any) {
      toast.error(e?.message || "Failed to build the confirmation");
    }
  };

  const apply = async () => {
    if (!preview?.digest || !openDraft) return;
    setApplying(true);
    try {
      await applyRebaseline(customerId, preview.rebaseline.id, preview.digest);
      toast.success("Rebaseline applied. The next client read is the new baseline.");
      setPreview(null);
      await load();
      onApplied();
    } catch (e: any) {
      toast.error(e?.message || "Apply was refused");
      // The plan may have moved, or a blocker appeared: show the fresh
      // confirmation, which lists the blockers, and require a new tick.
      setConfirmed(false);
      try { setPreview(await previewRebaseline(customerId, preview.rebaseline.id)); } catch { /* noop */ }
    } finally {
      setApplying(false);
    }
  };

  const cancelDraft = async () => {
    if (!openDraft) { setDraft(null); return; }
    try {
      await cancelRebaseline(customerId, openDraft.id);
      toast.info("Draft cancelled.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to cancel the draft");
    }
  };

  return (
    <div className="border-t" style={{ borderColor: "var(--border)" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3"
        style={{ background: "transparent", border: "none", cursor: "pointer" }}
      >
        <span className="flex items-center gap-2" style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
          <GitCommitVertical className="w-4 h-4" style={{ color: VIOLET }} />
          Series rebaselines
          {current && (
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--muted-foreground)" }}>
              battery v{current.battery_version}{appliedCount ? ` · ${appliedCount} applied` : ""}
              {openDraft ? " · draft open" : ""}
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: 0 }}>
            One planned change to this client's series: pins, lanes, questions and beliefs together,
            with a note the client sees. Applied once, taken by the next client read (the new
            baseline). Past reads and their scores are never touched, and an applied rebaseline
            cannot be edited.
          </p>

          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {unavailable && (
            <p style={{ fontSize: "var(--text-xs)", color: "var(--infrared-coral)", margin: 0 }}>{unavailable}</p>
          )}

          {/* History: applied and cancelled, read-only. */}
          {history.map((rb) => <HistoryItem key={rb.id} rebaseline={rb} />)}

          {current && !draft && !loading && (
            <button style={btn(true)} onClick={() => setDraft(emptyDraft(current))}>
              <Plus className="w-3.5 h-3.5" /> Plan a rebaseline
            </button>
          )}

          {current && draft && (
            <DraftEditor
              current={current}
              draft={draft}
              setDraft={setDraft}
              saving={saving}
              isSaved={!!openDraft}
              onSave={saveDraft}
              onReview={review}
              onCancel={cancelDraft}
            />
          )}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent style={{ maxWidth: 640, maxHeight: "85vh", overflowY: "auto" }}>
          <DialogHeader>
            <DialogTitle>Apply this rebaseline?</DialogTitle>
            <DialogDescription>
              Everything below is written at once and takes effect at the next client read, which
              becomes the series' new baseline (battery v{preview?.changes.battery_version_after ?? "?"}).
              Past reads and scores are not touched. This cannot be undone or edited afterwards.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3" style={{ fontSize: "var(--text-sm)" }}>
              <div>
                <span style={label}>Note (shown to the client)</span>
                <div style={{ ...box, padding: "8px 10px" }}>{preview.rebaseline.note || <em>none</em>}</div>
              </div>
              <div>
                <span style={label}>What changes ({preview.lines.length})</span>
                <ul style={{ margin: 0, paddingLeft: 18, listStyle: "disc", overflowWrap: "anywhere" }}>
                  {preview.lines.map((line) => <li key={line}>{line}</li>)}
                </ul>
              </div>
              <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                Lanes after: {preview.changes.lanes_after.join(", ")} ·
                {" "}questions {preview.changes.query_count_before} → {preview.changes.query_count_after} ·
                {" "}rubric {preview.changes.rubric_version_before} → {preview.changes.rubric_version_after}
              </div>
              {preview.warnings.map((w) => (
                <div key={w} style={{ fontSize: 12, color: "var(--warning-text)", background: "var(--warning-bg)", border: "1px solid var(--warning-border)", borderRadius: 6, padding: "6px 10px" }}>
                  {w}
                </div>
              ))}
              {preview.blockers.length > 0 && (
                <div style={{ fontSize: 12, color: "var(--infrared-coral)", border: "1px solid var(--infrared-coral)", borderRadius: 6, padding: "6px 10px" }}>
                  <div className="flex items-center gap-1.5" style={{ fontWeight: 600 }}>
                    <ShieldAlert className="w-3.5 h-3.5" /> Cannot apply yet
                  </div>
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18, listStyle: "disc" }}>
                    {preview.blockers.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                </div>
              )}
              {preview.blockers.length === 0 && (
                <label className="flex items-start gap-2" style={{ cursor: "pointer", fontSize: 12 }}>
                  <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ marginTop: 2, accentColor: VIOLET }} />
                  I have checked every change above. Apply it to this client's series.
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <button style={btn()} onClick={() => setPreview(null)} disabled={applying}>Back</button>
            <button
              style={btn(true, !confirmed || applying || !!preview?.blockers.length)}
              disabled={!confirmed || applying || !!preview?.blockers.length}
              onClick={apply}
            >
              {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitCommitVertical className="w-3.5 h-3.5" />}
              Apply rebaseline
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HistoryItem({ rebaseline }: { rebaseline: SeriesRebaseline }) {
  const applied = rebaseline.status === "applied";
  return (
    <div style={{ ...box, padding: "8px 12px", opacity: applied ? 1 : 0.6, fontSize: 12, lineHeight: 1.5 }}>
      <div className="flex items-center gap-2 flex-wrap" style={{ fontWeight: 600 }}>
        <GitCommitVertical className="w-3.5 h-3.5" style={{ color: VIOLET }} />
        {applied
          ? `Applied ${formatRebaselineDate(rebaseline.applied_at)} · battery v${rebaseline.battery_version}`
          : `Cancelled draft ${formatRebaselineDate(rebaseline.cancelled_at)}`}
        {applied && (
          <span style={{ fontWeight: 500, color: "var(--muted-foreground)" }}>
            {rebaseline.applied_by ? `by ${rebaseline.applied_by} · ` : ""}
            {rebaseline.first_run_id ? `taken by run id ${rebaseline.first_run_id}` : "waiting for the next client read"}
          </span>
        )}
      </div>
      {rebaseline.note && <div style={{ color: "var(--muted-foreground)" }}>{rebaseline.note}</div>}
      {applied && rebaseline.lines.length > 0 && (
        <ul style={{ margin: "2px 0 0", paddingLeft: 18, listStyle: "disc", overflowWrap: "anywhere" }}>
          {rebaseline.lines.map((line) => <li key={line}>{line}</li>)}
        </ul>
      )}
    </div>
  );
}

function DraftEditor({
  current, draft, setDraft, saving, isSaved, onSave, onReview, onCancel,
}: {
  current: CurrentSeries;
  draft: DraftState;
  setDraft: (d: DraftState) => void;
  saving: boolean;
  isSaved: boolean;
  onSave: () => void;
  onReview: () => void;
  onCancel: () => void;
}) {
  const set = (patch: Partial<DraftState>) => setDraft({ ...draft, ...patch });
  const toggleLane = (lane: string) => set({
    lanes: draft.lanes.includes(lane) ? draft.lanes.filter((l) => l !== lane) : [...draft.lanes, lane],
  });
  const [showQuestions, setShowQuestions] = useState(false);
  const [showBeliefs, setShowBeliefs] = useState(false);

  return (
    <div style={{ ...box, padding: 12, borderColor: "var(--spectral-violet-30)" }} className="space-y-3">
      <div className="flex items-center justify-between">
        <span style={{ fontSize: "var(--text-sm)", fontWeight: 700 }}>
          Rebaseline draft → battery v{current.battery_version + 1}
        </span>
        {current.read_in_progress && (
          <span style={{ fontSize: 11, color: "var(--warning-text)" }}>
            Read in progress: {current.read_in_progress} Apply is blocked until it finishes.
          </span>
        )}
      </div>

      <div>
        <span style={label}>Why (note shown to the client)</span>
        <textarea
          value={draft.note}
          onChange={(e) => set({ note: e.target.value })}
          rows={2}
          placeholder="e.g. Contract signed: Perplexity moved to Low, current model generations, Google AI Mode added."
          style={{ ...input, resize: "vertical" }}
        />
      </div>

      {/* Lanes and pins */}
      <div>
        <span style={label}>Lanes and pins</span>
        <div className="space-y-1">
          {current.available_lanes.map((lane) => {
            const on = draft.lanes.includes(lane.id);
            const was = current.lanes.includes(lane.id);
            const currentPin = current.pins[lane.id];
            return (
              <div key={lane.id} className="flex items-center gap-2 flex-wrap" style={{ fontSize: 12 }}>
                <label className="flex items-center gap-1.5" style={{ minWidth: 190, cursor: "pointer" }}>
                  <input type="checkbox" checked={on} onChange={() => toggleLane(lane.id)} style={{ accentColor: VIOLET }} />
                  <span style={{ fontWeight: 600 }}>{lane.label}</span>
                  {on && !was && <span style={{ color: VIOLET, fontWeight: 600 }}>added</span>}
                  {!on && was && <span style={{ color: "var(--infrared-coral)", fontWeight: 600 }}>removed</span>}
                </label>
                {on && !lane.unversioned && (
                  <select
                    value={draft.pins[lane.id] ?? ""}
                    onChange={(e) => set({ pins: { ...draft.pins, [lane.id]: e.target.value } })}
                    style={{ ...input, width: "auto", padding: "3px 8px", fontSize: 12 }}
                  >
                    <option value="">{currentPin ? `Keep ${currentPin}` : "Current default"}</option>
                    {lane.choices.filter((c) => c !== currentPin).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                )}
                {on && lane.unversioned && (
                  <span style={{ color: "var(--muted-foreground)" }}>unversioned (no pin)</span>
                )}
                {on && lane.id === CLAUDE_LANE && current.claude_search_tool && (
                  <select
                    aria-label="Claude search tool version"
                    value={draft.claudeSearchTool}
                    onChange={(e) => set({ claudeSearchTool: e.target.value })}
                    style={{ ...input, width: "auto", padding: "3px 8px", fontSize: 12 }}
                  >
                    <option value="">{`Keep search tool ${current.claude_search_tool}`}</option>
                    {(current.claude_search_tool_choices ?? [])
                      .filter((c) => c !== current.claude_search_tool)
                      .map((c) => (
                        <option key={c} value={c}>{`Search tool ${c}`}</option>
                      ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Questions */}
      <div>
        <button style={{ ...btn(), padding: "3px 8px" }} onClick={() => setShowQuestions((s) => !s)}>
          Questions ({current.queries.length})
          {Object.keys(draft.questionEdits).length + draft.questionRemovals.length + draft.questionAdds.length > 0 &&
            ` · ${Object.keys(draft.questionEdits).length} edited, ${draft.questionRemovals.length} removed, ${draft.questionAdds.length} added`}
          {showQuestions ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {showQuestions && (
          <div className="space-y-1.5 mt-2">
            {current.queries.map((q) => {
              const removed = draft.questionRemovals.includes(q.id);
              const edit = draft.questionEdits[q.id];
              return (
                <div key={q.id} className="flex items-start gap-2" style={{ fontSize: 12 }}>
                  <span style={{ width: 34, fontWeight: 600, color: "var(--muted-foreground)", paddingTop: 6 }}>{q.id}</span>
                  {edit ? (
                    <textarea
                      value={edit.text}
                      rows={2}
                      onChange={(e) => set({ questionEdits: { ...draft.questionEdits, [q.id]: { text: e.target.value } } })}
                      style={{ ...input, fontSize: 12, flex: 1, resize: "vertical" }}
                    />
                  ) : (
                    <span style={{ flex: 1, paddingTop: 6, textDecoration: removed ? "line-through" : "none", color: removed ? "var(--muted-foreground)" : "var(--foreground)" }}>
                      {q.text}
                    </span>
                  )}
                  {!removed && !edit && (
                    <button style={{ ...btn(), padding: "2px 8px" }} onClick={() => set({ questionEdits: { ...draft.questionEdits, [q.id]: { text: q.text } } })}>Edit</button>
                  )}
                  {edit && (
                    <button title="Discard edit" style={{ ...btn(), padding: "2px 6px" }} onClick={() => {
                      const { [q.id]: _gone, ...rest } = draft.questionEdits;
                      set({ questionEdits: rest });
                    }}><Undo2 className="w-3 h-3" /></button>
                  )}
                  {!edit && (
                    <button
                      title={removed ? "Keep this question" : "Remove this question"}
                      style={{ ...btn(), padding: "2px 6px" }}
                      onClick={() => set({
                        questionRemovals: removed
                          ? draft.questionRemovals.filter((x) => x !== q.id)
                          : [...draft.questionRemovals, q.id],
                      })}
                    >
                      {removed ? <Undo2 className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
                    </button>
                  )}
                </div>
              );
            })}
            {draft.questionAdds.map((add, i) => (
              <div key={`add-${i}`} className="flex items-start gap-2" style={{ fontSize: 12 }}>
                <span style={{ width: 34, fontWeight: 600, color: VIOLET, paddingTop: 6 }}>new</span>
                <textarea
                  value={add.text}
                  rows={2}
                  placeholder="Question text"
                  onChange={(e) => set({ questionAdds: draft.questionAdds.map((a, j) => j === i ? { ...a, text: e.target.value } : a) })}
                  style={{ ...input, fontSize: 12, flex: 1, resize: "vertical" }}
                />
                <select
                  value={add.category}
                  onChange={(e) => set({ questionAdds: draft.questionAdds.map((a, j) => j === i ? { ...a, category: e.target.value } : a) })}
                  style={{ ...input, width: "auto", fontSize: 12, padding: "3px 6px" }}
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button style={{ ...btn(), padding: "2px 6px" }} onClick={() => set({ questionAdds: draft.questionAdds.filter((_, j) => j !== i) })}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button style={{ ...btn(), padding: "3px 8px" }} onClick={() => set({ questionAdds: [...draft.questionAdds, { text: "", category: "direct" }] })}>
              <Plus className="w-3 h-3" /> Add a question
            </button>
          </div>
        )}
      </div>

      {/* Beliefs / rubric */}
      <div>
        <button style={{ ...btn(), padding: "3px 8px" }} onClick={() => setShowBeliefs((s) => !s)}>
          Beliefs and rubric ({current.reasoning_variables.length})
          {Object.keys(draft.variableEdits).length + draft.variableRemovals.length + draft.variableAdds.length > 0 &&
            ` · ${Object.keys(draft.variableEdits).length} edited, ${draft.variableRemovals.length} removed, ${draft.variableAdds.length} added`}
          {showBeliefs ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {showBeliefs && (
          <div className="space-y-1.5 mt-2">
            {current.reasoning_variables.map((v) => {
              const removed = draft.variableRemovals.includes(v.id);
              const edit = draft.variableEdits[v.id];
              return (
                <div key={v.id} style={{ fontSize: 12 }}>
                  <div className="flex items-start gap-2">
                    <span style={{ flex: "0 0 auto", whiteSpace: "nowrap", fontWeight: 600, color: "var(--muted-foreground)" }}>{v.id}</span>
                    <span style={{ flex: 1, textDecoration: removed ? "line-through" : "none" }}>{v.claim}</span>
                    {!removed && !edit && (
                      <button style={{ ...btn(), padding: "2px 8px" }} onClick={() => set({
                        variableEdits: {
                          ...draft.variableEdits,
                          [v.id]: { claim: v.claim ?? "", score_2: v.score_2 ?? "", score_1: v.score_1 ?? "", score_0: v.score_0 ?? "" },
                        },
                      })}>Edit</button>
                    )}
                    {edit && (
                      <button title="Discard edit" style={{ ...btn(), padding: "2px 6px" }} onClick={() => {
                        const { [v.id]: _gone, ...rest } = draft.variableEdits;
                        set({ variableEdits: rest });
                      }}><Undo2 className="w-3 h-3" /></button>
                    )}
                    {!edit && (
                      <button
                        title={removed ? "Keep this belief" : "Remove this belief"}
                        style={{ ...btn(), padding: "2px 6px" }}
                        onClick={() => set({
                          variableRemovals: removed
                            ? draft.variableRemovals.filter((x) => x !== v.id)
                            : [...draft.variableRemovals, v.id],
                        })}
                      >
                        {removed ? <Undo2 className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
                      </button>
                    )}
                  </div>
                  {edit && (
                    <div className="grid gap-1.5 mt-1" style={{ marginLeft: 42 }}>
                      {VARIABLE_FIELDS.map((f) => (
                        <label key={f} style={{ fontSize: 11 }}>
                          <span style={{ color: "var(--muted-foreground)", fontWeight: 600 }}>{f}</span>
                          <textarea
                            value={edit[f]}
                            rows={f === "claim" ? 1 : 2}
                            onChange={(e) => set({ variableEdits: { ...draft.variableEdits, [v.id]: { ...edit, [f]: e.target.value } } })}
                            style={{ ...input, fontSize: 12, resize: "vertical" }}
                          />
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {draft.variableAdds.map((add, i) => (
              <div key={`vadd-${i}`} className="flex items-start gap-2" style={{ fontSize: 12 }}>
                <input
                  value={add.id}
                  placeholder="id, e.g. rv8"
                  onChange={(e) => set({ variableAdds: draft.variableAdds.map((a, j) => j === i ? { ...a, id: e.target.value } : a) })}
                  style={{ ...input, width: 90, fontSize: 12 }}
                />
                <input
                  value={add.claim}
                  placeholder="Claim"
                  onChange={(e) => set({ variableAdds: draft.variableAdds.map((a, j) => j === i ? { ...a, claim: e.target.value } : a) })}
                  style={{ ...input, flex: 1, fontSize: 12 }}
                />
                <button style={{ ...btn(), padding: "2px 6px" }} onClick={() => set({ variableAdds: draft.variableAdds.filter((_, j) => j !== i) })}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button style={{ ...btn(), padding: "3px 8px" }} onClick={() => set({ variableAdds: [...draft.variableAdds, { id: "", claim: "" }] })}>
              <Plus className="w-3 h-3" /> Add a belief
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2" style={{ borderTop: "1px solid var(--border)" }}>
        <button style={btn()} onClick={onCancel} disabled={saving}>
          <X className="w-3.5 h-3.5" /> {isSaved ? "Cancel draft" : "Discard"}
        </button>
        <div className="flex gap-2">
          <button style={btn(false, saving)} onClick={onSave} disabled={saving}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save draft
          </button>
          <button style={btn(true, saving)} onClick={onReview} disabled={saving}>
            <GitCommitVertical className="w-3.5 h-3.5" /> Review & apply…
          </button>
        </div>
      </div>
    </div>
  );
}
